/**
 * PHI transport decryption — the browser half of `be/src/lib/crypto/transport.ts`.
 *
 * ── The design decision that matters ────────────────────────────────────────
 *
 * Unsealing happens in the axios RESPONSE INTERCEPTOR, not in components. By the
 * time any hook, store or component sees a response body, every sealed value has
 * already been replaced by its plaintext. The rest of the frontend is therefore
 * completely unaware that transport sealing exists — no `await unseal(...)` at
 * render sites, no async in a render path, no per-field opt-in list to keep in
 * sync with the backend, and no possibility of a component forgetting and
 * rendering `MCT1.q7Fh…` at a user.
 *
 * That last point is what rules out the alternative. Decrypting per render site
 * would mean every new PHI field needs someone to remember, and forgetting shows
 * up as base64 on screen — caught only if a human happens to look at that view.
 *
 * ── What this can and cannot reveal ─────────────────────────────────────────
 *
 * The server masks BEFORE it seals, so this key opens only what the server had
 * already decided this user may see. A user who digs this key out of the JS heap
 * decrypts "B****** T*****" and learns nothing. Masking is the authorization
 * boundary; sealing is confidentiality on the wire. Do not invert that.
 *
 * ── Why the key is never persisted ──────────────────────────────────────────
 *
 * Memory only — never localStorage or sessionStorage, which any XSS can read and
 * which outlive the session. This mirrors how the access token is already handled
 * in `shared/api/client.ts`. A page refresh drops the key, so we re-fetch it from
 * `GET /auth/phi-key`; the key is derived server-side from the session, so there
 * is nothing stored to look up and re-fetching is cheap.
 */

/** Wire-format marker — must match TRANSPORT_PREFIX on the server. */
const TRANSPORT_PREFIX = 'MCT1.'
const IV_BYTES = 12

interface PhiKeyResponse {
  enabled: boolean
  key?: string
  aad?: string
}

let cryptoKey: CryptoKey | null = null
// `Uint8Array<ArrayBuffer>`, not plain `Uint8Array`. Since TS 5.7 the type is
// generic over its backing buffer and defaults to `ArrayBufferLike`, which
// includes SharedArrayBuffer — but WebCrypto's `BufferSource` requires a plain
// `ArrayBuffer`. A bare `Uint8Array` annotation therefore fails to satisfy
// subtle.decrypt()'s parameter types.
let aad: Uint8Array<ArrayBuffer> | null = null
/** null = not yet determined. Set once the key endpoint has answered. */
let sealingEnabled: boolean | null = null
/** De-dupes concurrent loads so a burst of parallel requests fetches one key. */
let loadPromise: Promise<void> | null = null

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64)
  // Allocate through an explicit ArrayBuffer so the result is narrowed to
  // `Uint8Array<ArrayBuffer>` and is accepted as a WebCrypto BufferSource.
  // `subarray()` preserves the buffer type parameter, so every slice taken from
  // this in unsealOne() stays assignable too.
  const out = new Uint8Array(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i)
  return out
}

/**
 * Fetch and import this session's transport key.
 *
 * Uses bare `fetch` rather than the shared axios instance on purpose: the axios
 * instance carries the response interceptor that calls back into this module, and
 * routing the key fetch through it would be a cycle — the key request would wait
 * on a key that is still being fetched.
 */
export async function loadPhiTransportKey(accessToken: string, baseUrl: string): Promise<void> {
  // Sealing is off server-side and will not turn on mid-session. Without this
  // early return, `isPhiTransportReady()` stays false forever and the response
  // interceptor re-fetches the key on EVERY response — one extra round trip per
  // request, for a feature that is switched off.
  if (sealingEnabled === false) return
  if (loadPromise) return loadPromise

  loadPromise = (async () => {
    try {
      const res = await fetch(`${baseUrl}/auth/phi-key`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      })
      if (!res.ok) {
        // Leave sealingEnabled null so the next response retries rather than
        // latching "off" — latching off would make sealed values render raw.
        return
      }
      const body = (await res.json()) as PhiKeyResponse
      if (!body.enabled || !body.key || !body.aad) {
        sealingEnabled = false
        return
      }
      cryptoKey = await crypto.subtle.importKey(
        'raw',
        base64ToBytes(body.key),
        { name: 'AES-GCM' },
        false, // non-extractable: keeps it out of casual devtools inspection
        ['decrypt'],
      )
      aad = new TextEncoder().encode(body.aad)
      sealingEnabled = true
    } finally {
      loadPromise = null
    }
  })()

  return loadPromise
}

/** Drop the key. Call on logout so it does not outlive the session. */
export function clearPhiTransportKey(): void {
  cryptoKey = null
  aad = null
  sealingEnabled = null
  loadPromise = null
}

/** Whether the key is loaded and usable. */
export function isPhiTransportReady(): boolean {
  return cryptoKey !== null && aad !== null
}

/** Whether a string is a sealed value. Cheap enough to run on every string. */
function isSealed(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(TRANSPORT_PREFIX)
}

async function unsealOne(sealed: string): Promise<string> {
  const buf = base64ToBytes(sealed.slice(TRANSPORT_PREFIX.length))
  const iv = buf.subarray(0, IV_BYTES)
  // The server appends the tag to the ciphertext (iv || ct || tag) precisely so
  // WebCrypto can take `ct || tag` as one buffer with no splicing here.
  const body = buf.subarray(IV_BYTES)
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, additionalData: aad!, tagLength: 128 },
    cryptoKey!,
    body,
  )
  return new TextDecoder().decode(plain)
}

/**
 * Recursively replace every sealed string in a response body with its plaintext.
 *
 * Two-pass by design. The first pass walks the structure and collects the sealed
 * values it finds; the second decrypts them all with a single `Promise.all` and
 * writes the results back. Decrypting inline during the walk would serialise
 * hundreds of awaits on a list response — a 100-row page with four PHI fields is
 * 400 round trips through the microtask queue, one after another. Batched, it is
 * one.
 *
 * Mutates in place: response bodies are freshly parsed per request and nothing
 * else holds a reference yet, so cloning would only add garbage.
 */
export async function unsealPhiDeep<T>(data: T): Promise<T> {
  if (!isPhiTransportReady() || data === null || typeof data !== 'object') return data

  const sealed: string[] = []
  const patches: Array<(plain: string) => void> = []

  const walk = (node: unknown, seen: Set<object>): void => {
    if (node === null || typeof node !== 'object') return
    // Guard against cycles. Response bodies are JSON and acyclic, but this module
    // also runs over anything a caller hands it, and an infinite walk would hang
    // the request rather than fail it.
    if (seen.has(node as object)) return
    seen.add(node as object)

    if (Array.isArray(node)) {
      node.forEach((item, i) => {
        if (isSealed(item)) {
          sealed.push(item)
          patches.push((plain) => {
            node[i] = plain
          })
        } else {
          walk(item, seen)
        }
      })
      return
    }

    const record = node as Record<string, unknown>
    for (const k of Object.keys(record)) {
      const v = record[k]
      if (isSealed(v)) {
        sealed.push(v)
        patches.push((plain) => {
          record[k] = plain
        })
      } else {
        walk(v, seen)
      }
    }
  }

  walk(data, new Set<object>())
  if (sealed.length === 0) return data

  const plains = await Promise.all(
    sealed.map((s) =>
      unsealOne(s).catch(() => {
        // A single undecryptable field must not blank an entire page. Most likely
        // cause is a key rotated mid-session; the em-dash matches how the backend
        // renders an absent PHI value, so the UI stays coherent.
        return '—'
      }),
    ),
  )
  plains.forEach((plain, i) => patches[i](plain))
  return data
}

/** Test seam: whether the endpoint reported sealing as on, off, or unknown. */
export function phiSealingEnabled(): boolean | null {
  return sealingEnabled
}
